import { type Dispatch, type ReactNode, type SetStateAction, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { Dialog, DialogActions, DialogContent, DialogHeader } from "~/common/mui/Dialog/Dialog";
import {
  type ConfiguredQuickEntryItem,
  type QuickEntryForm,
  type QuickEntryItemPC,
  type QuickEntryPCForm,
} from "../types/models";
import { hasProductConfigured } from "../utils/helpers";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import CenterContents from "~/common/components/CenterContents";
import classNames from "classnames";
import { LinkButton } from "~/common/components/Link";
import useMediaQuery, { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { Button, Stack, Typography, styled } from "~/common/mui";
import { getEmptyRow } from "~/features/quickEntry/constants/constants";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { IconClickable } from "~/common/components/icon/IconClickable";
import { IconType } from "~/common/components/icon/IconType";
import { QuickEntryTableRow } from "~/features/quickEntry/v2/QuickEntryTableRow";
import { Icon } from "~/common/components/icon/Icon";
import { useTranslation } from "react-i18next";

type Fields = "quantity" | "requestedDeliveryDate" | "comment" | "priceReference";

interface Props {
  onClose: () => void;
  // Max amount of products that can be added, 0 or undefined for no limit
  maxAmount?: number;
  maxAmountMessage?: ValueProvider<ReactNode>;
  validateRow?: (entry: ConfiguredQuickEntryItem, itemsPopup?: QuickEntryItemPC[]) => ReactNode[];

  onAdd: (form: QuickEntryPCForm, setIsSending: Dispatch<SetStateAction<boolean>>, onClose: () => void) => void;
  // additionalFields?: Fields[];
  variant?: "pc" | "wishlist" | "b2c-cart";
  disableAdd?: boolean;
  initialEmptyRowAmount?: number;
  addButtonLabel: ReactNode;
}

export const QuickEntryPopup = ({
  onClose,
  maxAmount = 0,
  validateRow,
  maxAmountMessage,
  onAdd,
  disableAdd,
  initialEmptyRowAmount = 1,
  variant = "pc",
  addButtonLabel,
}: Props) => {
  const { t } = useTranslation();

  const isMinTablet = useIsMinTabletSize();
  const isPhone = !isMinTablet;
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const defaultValues = useState(() => {
    const form: QuickEntryForm = { items: [] };
    for (let i = 0; i < initialEmptyRowAmount; i++) {
      form.items.push(getEmptyRow());
    }
    return form;
  })[0];

  const formProps = useForm<QuickEntryForm>({
    mode: "onBlur",
    defaultValues: defaultValues,
    shouldUnregister: false,
  });
  const { fields, remove, append } = useFieldArray({ control: formProps.control, name: "items" });

  const [isSending, setSending] = useState(false);

  const formRef = useRef<HTMLButtonElement>(null);
  const onSubmitClick = () => {
    return formRef.current?.click();
  };

  const watchItemsArray = formProps.watch("items");
  const items = fields.map((field, index) => {
    return {
      ...field,
      ...watchItemsArray[index],
    };
  });

  const itemsWithConfiguredProduct: ConfiguredQuickEntryItem[] = items.filter(
    hasProductConfigured,
  ) as ConfiguredQuickEntryItem[];

  const containErrors = !!formProps.formState.errors.items;

  const isAddToCartDisabled =
    itemsWithConfiguredProduct.length === 0 ||
    containErrors ||
    disableAdd ||
    (validateRow && itemsWithConfiguredProduct.some((row) => validateRow(row, items).length > 0));

  const onRemoveAllItems = () => {
    formProps.setValue("items", [getEmptyRow()]);
  };

  const maxReached = maxAmount && fields.length >= maxAmount;

  const appendNextRow = () => {
    if (!maxReached) {
      append(getEmptyRow());
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={"desktop-l"}
      slotProps={{
        paper: {
          className: classNames(
            "scha__modal__quick-entry",
            variant === "pc" && "scha__quick-entry--pc",
            ["wishlist", "b2c-cart"].includes(variant) && "scha__quick-entry--wishlist",
          ),
          sx: {
            margin: { phone: 0, tablet: "32px" },
            maxHeight: { phone: "100dvh", tablet: "90%" },
            height: { phone: "100dvh", tablet: "auto" },
            maxWidth: "1200px",
          },
        },
      }}
    >
      <StyledDialogHeader onClose={onClose}>
        <Typography variant={"headlineSmall"}>{t("modal.quickEntry.title")}</Typography>
      </StyledDialogHeader>

      <StyledDialogContent>
        <FormProvider {...formProps}>
          <form
            onSubmit={formProps.handleSubmit((form) => onAdd(form, setSending, onClose))}
            className={isSending ? "v-hidden" : undefined}
          >
            <button ref={formRef} type={"submit"} hidden />
            <div className={classNames("scha__quick-entry__table")}>
              {isMinTablet && (
                <div className={"scha__quick-entry__table__header"}>
                  {!isPhone && <div>{t("wishlistPage.table.header.number")}</div>}
                  <div className={classNames("d-flex", isPhone && "flex-1")}>
                    {t("bulkUpload.table.header.productId")}
                  </div>
                  {["wishlist", "b2c-cart"].includes(variant) && <div>{t("cartPage.table.header.quantity.title")}</div>}

                  <div className={classNames("d-flex flex-justify-end", !isPhone && "flex-1")}>
                    <IconClickable
                      type={IconType.Bin}
                      onIconClick={onRemoveAllItems}
                      data-test-id={"btn-quick-remove-all"}
                    />
                  </div>
                </div>
              )}
              <div className={"scha__quick-entry__table__body"}>
                {items.map((item, index) => (
                  <QuickEntryTableRow
                    key={item.id}
                    item={item}
                    index={index}
                    onRemove={() => {
                      if (fields.length > 1) {
                        remove(index);
                      } else {
                        onRemoveAllItems();
                      }
                    }}
                    items={items}
                    validateRow={validateRow}
                    variant={variant}
                  />
                ))}
              </div>
              <div className="scha__quick-entry__table__append-action-row add-new-row-button">
                {isMinTablet && <div className="scha__quick-entry__table__left-side" />}
                {!maxReached ? (
                  <LinkButton
                    type={"button"}
                    onFocus={isDesktop ? appendNextRow : undefined}
                    onClick={!isDesktop ? appendNextRow : undefined}
                    data-test-id={"btn-quick-entry-add-new-product"}
                    iconBefore={<Icon type={IconType.Plus} />}
                  >
                    {t("modal.quickEntry.newRow")}
                  </LinkButton>
                ) : (
                  render(maxAmountMessage)
                )}
              </div>
            </div>
          </form>

          {isSending && (
            <CenterContents className={"scha__quick-entry__spinner"}>
              <CircleSpinner />
            </CenterContents>
          )}
        </FormProvider>
      </StyledDialogContent>

      <DialogActions>
        <Stack direction="row" gap={1}>
          <Button variant={"text"} onClick={onClose} data-test-id={"btn-quick-entry-cancel"}>
            {t("cancel")}
          </Button>
          <Button onClick={onSubmitClick} disabled={isAddToCartDisabled} data-test-id={"btn-quick-entry-submit"}>
            {addButtonLabel}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

type ValueProvider<T> = T | (() => T);
function render(val: ValueProvider<ReactNode>) {
  return typeof val === "function" ? val() : val;
}

const StyledDialogHeader = styled(DialogHeader)({
  paddingBottom: "16px",
  marginBottom: "0",
});

const StyledDialogContent = styled(DialogContent)({
  paddingTop: "0",
});
